import axios from 'axios';
import React, { Fragment, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IoMdArrowRoundBack } from "react-icons/io";
import { useDispatch, useSelector } from 'react-redux';
import { addItem, increaseQuantity, decreaseQuantity } from '../redux/cart';
import { FaStar, FaRegStar, FaStarHalfAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { ToastContainer, Zoom, toast } from 'react-toastify';

function ProductPage() {
    const { id } = useParams();
    const [productData, setProductData] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);
    const [images, setImages] = useState([]); // ordered image list
    const mainImgRef = useRef(null);
    const [similarProducts, setSimilarProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [descExpanded, setDescExpanded] = useState(false);
    const dispatch = useDispatch();
    const cartItems = useSelector(state => state.cart.items || []);
    const API_URL = process.env.REACT_APP_API_URL;

    const fetchSimilarProducts = async (category, name) => {
        try {
            const response = await axios.get(`${API_URL}/products-by-category/${category}`);
            const filteredProducts = response.data.filter((data) => data.name !== name);
            setSimilarProducts(filteredProducts || []);
        } catch (err) {
            console.error("Error fetching similar products:", err);
        }
    };

    const handleFetchProduct = async () => {
        setLoading(true); // Show loading spinner during data fetching
        try {
            const response = await axios.get(`${API_URL}/single-product-data/${id}`);
            setProductData(response.data || {});
            const imgs = [response.data.image1, response.data.image2, response.data.image3, response.data.image4].filter(Boolean);
            setImages(imgs);
            setSelectedImage(imgs[0] || null);
            fetchSimilarProducts(response.data.category, response.data.name);
        } catch (err) {
            console.error("Error fetching product:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleFetchProduct();
    }, [id]); // Refetch product details when `id` changes

    const toastConfig = {
        autoClose: 500,
        hideProgressBar: true,
        transition: Zoom,
        icon: false,
        closeButton: false,
        style: {
            width: '420px',
            border: '1px solid #bbbbbb',
            boxShadow: '0 0 21px #ed3237',
            textAlign: 'center',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ed3237',
            fontSize: '16px'
        }
    };

    const handleAddToCart = useCallback((product) => {
        dispatch(addItem(product));
        toast.success('Item added to cart', toastConfig);
    }, [dispatch]);
    // Derived index of current image
    const currentIndex = useMemo(() => images.findIndex(i => i === selectedImage), [images, selectedImage]);

    const showImage = useCallback((idx) => {
        if (idx < 0 || idx >= images.length) return;
        setSelectedImage(images[idx]);
    }, [images]);

    const handlePrev = useCallback(() => {
        if (images.length < 2) return;
        const next = (currentIndex - 1 + images.length) % images.length;
        showImage(next);
    }, [currentIndex, images, showImage]);

    const handleNext = useCallback(() => {
        if (images.length < 2) return;
        const next = (currentIndex + 1) % images.length;
        showImage(next);
    }, [currentIndex, images, showImage]);

    // Keyboard navigation (Left/Right + Escape to blur)
    const handleKeyNav = useCallback((e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); handlePrev(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); handleNext(); }
        else if (e.key === 'Escape') { mainImgRef.current && mainImgRef.current.blur(); }
    }, [handlePrev, handleNext]);

    useEffect(() => {
        // If productData changed externally (manual edits), refresh images list
        const list = [productData.image1, productData.image2, productData.image3, productData.image4].filter(Boolean);
        if (list.length && (list.join('|') !== images.join('|'))) {
            setImages(list);
            if (!list.includes(selectedImage)) setSelectedImage(list[0]);
        }
    }, [productData, images, selectedImage]);

    const handleInc = useCallback(id => dispatch(increaseQuantity(id)), [dispatch]);
    const handleDec = useCallback(id => dispatch(decreaseQuantity(id)), [dispatch]);

    // Derived pricing + rating data for main product
    const original = Number(productData.originalPrice || productData.price || 0);
    const currentPrice = Number(productData.price || 0);
    const hasDiscount = original > currentPrice && original > 0;
    const discountPct = hasDiscount ? Math.round(((original - currentPrice) / original) * 100) : 0;
    const ratingVal = Number(productData.rating || 0);
    const mainStars = useMemo(() => {
        const arr = [];
        for (let i = 1; i <= 5; i++) {
            if (ratingVal >= i) arr.push(<FaStar key={i} className='text-warning' />);
            else if (ratingVal >= i - 0.5) arr.push(<FaStarHalfAlt key={i} className='text-warning' />);
            else arr.push(<FaRegStar key={i} className='text-warning' />);
        }
        return arr;
    }, [ratingVal]);

    const DESC_LIMIT = 260;
    const descText = productData.description || '';
    const isLongDesc = descText.length > DESC_LIMIT;
    const formattedDescription = useMemo(() => {
        if (!descText) return null;
        const toggleBtn = (expanded) => (
            isLongDesc ? (
                <button
                    key="toggle"
                    type="button"
                    className="read-toggle"
                    aria-expanded={expanded}
                    onClick={() => setDescExpanded(e => !e)}
                >{expanded ? 'Read Less' : 'Read More'}</button>
            ) : null
        );

        if (!descExpanded && isLongDesc) {
            const truncated = descText.slice(0, DESC_LIMIT).trim();
            return (
                <p className="prod-desc-body mb-0">
                    {truncated}... {toggleBtn(false)}
                </p>
            );
        }
        // Expanded or short: maintain paragraphs; append toggle to last paragraph if long
        const segments = descText.split(/\n+|(?<=[.!?])\s{2,}/).filter(Boolean);
        if (segments.length <= 1) {
            return (
                <p className="prod-desc-body mb-0">
                    {descText} {toggleBtn(true)}
                </p>
            );
        }
        return (
            <div className="prod-desc-body">
                {segments.map((seg, i) => {
                    const isLast = i === segments.length - 1;
                    return (
                        <p key={i} className={`mb-2 last-m-0${isLast ? ' mb-0' : ''}`}>
                            {seg.trim()} {isLast && toggleBtn(true)}
                        </p>
                    );
                })}
            </div>
        );
    }, [descText, descExpanded, isLongDesc]);

    return (
        <Fragment>
            <div className="container-fluid m-t25">
                <div className="row">
                    <div className="col-12 cat-strip border shadow-sm z-1">
                        <div>
                            <button onClick={() => window.history.back()} aria-label="Go Back">
                                <IoMdArrowRoundBack />
                            </button>
                            <label>{productData?.name && productData.name.toUpperCase()}</label>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-spinner">
                    <div className="spinner-border text-danger" role="status">
                        <span className="visually-hidden">Loading product details...</span>
                    </div>
                </div>
            ) : (
                <div className="container-fluid p-0">
                    <div className="row g-0 mx-0">
                        <div className="col-12 p-0">
                            <div className='full-bleed-gallery'>
                                <div className='product-gallery'>
                                <div className='product-gallery-main' tabIndex={0} onKeyDown={handleKeyNav} ref={mainImgRef} aria-label='Product image viewer'>
                                    {images.length > 1 && (
                                        <button type='button' className='gallery-nav-btn prev' aria-label='Previous image' onClick={handlePrev} disabled={images.length<2}>
                                            <FaChevronLeft />
                                        </button>
                                    )}
                                    {selectedImage ? (
                                        <img src={`${API_URL}/${selectedImage}`} alt={productData.name || 'Product'} />
                                    ) : <div className='no-image small text-muted'>No image</div> }
                                    {images.length > 1 && (
                                        <button type='button' className='gallery-nav-btn next' aria-label='Next image' onClick={handleNext} disabled={images.length<2}>
                                            <FaChevronRight />
                                        </button>
                                    )}
                                </div>
                                {images.length > 0 && (
                                    <div className='thumb-strip' role='listbox' aria-label='Product image thumbnails'>
                                        {images.map((img, idx) => (
                                            <button
                                                key={idx}
                                                type='button'
                                                className={'thumb-btn'+(img===selectedImage?' active':'')}
                                                onClick={()=>showImage(idx)}
                                                aria-label={`Image ${idx+1} of ${images.length}`}
                                                aria-selected={img===selectedImage}
                                            >
                                                <img src={`${API_URL}/${img}`} alt='' />
                                            </button>
                                        ))}
                                    </div>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12 col-lg-8 px-2">
                            <section className="product-summary card-like   p-3 p-sm-4">
                                <header className="mb-3">
                                    <h1 className="h3 product-title mb-2">{productData?.name && productData.name.toUpperCase()}</h1>
                                    <div className="d-flex flex-wrap align-items-center gap-2 small text-muted">
                                        {productData.brand && <span className="badge bg-light text-dark fw-normal border">Brand: {productData.brand}</span>}
                                        {productData.category && <span className="badge bg-light text-dark fw-normal border">Category: {productData.category}</span>}
                                        {productData.quantity && productData.unit && <span className="badge bg-light text-dark fw-normal border">Pack: {productData.quantity}{productData.unit}</span>}
                                    </div>
                                </header>
                                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-4 mb-3">
                                    <div className="price-block">
                                        <div className="d-flex align-items-end gap-2 flex-wrap">
                                            <span className="current-price">₹{currentPrice.toFixed(2)}</span>
                                            {hasDiscount && (
                                                <>
                                                    <span className="original-price">₹{original.toFixed(2)}</span>
                                                    <span className="discount-pill" aria-label={`${discountPct}% off`}>
                                                        {discountPct}% OFF
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {hasDiscount && (
                                            <div className="saving-line text-success small mt-1">You save ₹{(original - currentPrice).toFixed(2)}</div>
                                        )}
                                    </div>
                                    <div className="rating-block d-flex flex-column justify-content-center">
                                        <div className="d-flex align-items-center gap-1 stars" aria-label={`Rated ${ratingVal} out of 5`}>
                                            {mainStars}
                                            {ratingVal > 0 && <span className="ms-1 small text-muted fw-semibold">{ratingVal.toFixed(1)}/5</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="prod-desc mb-3">
                                    <h2 className="h6 text-uppercase text-muted mb-2">Description</h2>
                                    {formattedDescription || <p className="text-muted mb-0">No description available.</p>}
                                </div>
                                <ul className="detail-list list-unstyled small mb-0">
                                    <li><span className="label">Product Name:</span><span className="value">{productData.name || 'N/A'}</span></li>
                                    <li><span className="label">Brand:</span><span className="value">{productData.brand || 'Unknown'}</span></li>
                                    <li><span className="label">Category:</span><span className="value">{productData.category || 'N/A'}</span></li>
                                    <li><span className="label">Pack Size:</span><span className="value">{(productData.quantity && productData.unit) ? `${productData.quantity}${productData.unit}` : 'N/A'}</span></li>
                                    <li><span className="label">MRP:</span><span className="value">{original ? `₹${original.toFixed(2)}` : 'N/A'}</span></li>
                                    <li><span className="label">Current Price:</span><span className="value text-danger fw-semibold">₹{currentPrice.toFixed(2)}</span></li>
                                    {hasDiscount && <li><span className="label">Savings:</span><span className="value text-success">₹{(original - currentPrice).toFixed(2)} ({discountPct}%)</span></li>}
                                    <li><span className="label">Rating:</span><span className="value">{ratingVal > 0 ? `${ratingVal.toFixed(1)}/5` : 'Not rated'}</span></li>
                                </ul>
                            </section>
                        </div>
                        {/* <div className="col-12 col-lg-4 mt-4 mt-lg-0 p-0">
                            <div className="purchase-cta card-like border p-3 p-sm-4 h-100 d-flex flex-column justify-content-between">
                                <div>
                                    <h2 className="h6 text-uppercase text-muted">Purchase</h2>
                                    <div className="mb-3">
                                        <span className="current-price display-6 d-block">₹{currentPrice.toFixed(2)}</span>
                                        {hasDiscount && <span className="original-price d-inline-block me-2 mt-1">₹{original.toFixed(2)}</span>}
                                        {hasDiscount && <span className="discount-pill align-middle mt-1">{discountPct}% OFF</span>}
                                    </div>
                                </div>
                                <button className="prod-button w-100" onClick={() => handleAddToCart(productData)}>ADD TO CART</button>
                            </div>
                        </div> */}
                    </div>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 cat-strip border shadow-sm">
                                <label>SIMILAR PRODUCTS</label>
                            </div>
                        </div>
                        <div className="row">
                            {similarProducts.length > 0 ? (
                                similarProducts.slice(0, 8).map((item, index) => {
                                    const original = Number(item.originalPrice || item.price || 0);
                                    const price = Number(item.price || 0);
                                    const hasDiscount = original > price && original > 0;
                                    const discountPct = hasDiscount ? Math.round(((original - price) / original) * 100) : 0;
                                    const ratingVal = Number(item.rating || 0);
                                    const stars = [];
                                    for (let i = 1; i <= 5; i++) {
                                        if (ratingVal >= i) stars.push(<FaStar key={i} className='text-warning' />); else if (ratingVal >= i - 0.5) stars.push(<FaStarHalfAlt key={i} className='text-warning' />); else stars.push(<FaRegStar key={i} className='text-warning' />);
                                    }
                                    const cartEntry = cartItems.find(ci => ci._id === item._id || ci.name === item.name);
                                    const inCart = !!cartEntry;
                                    return (
                                        <div className="col-lg-3 col-md-3 col-sm-4 col-6 prod-list border text-center position-relative" key={item._id || index}>
                                            <Link
                                                to={`/product/${item.name}`}
                                                className="d-block w-100 position-relative"
                                                onClick={() => handleFetchProduct(item._id)}
                                            >
                                                {hasDiscount && (
                                                    <span className='discount-badge' aria-label={`${discountPct}% discount`}>{discountPct}%</span>
                                                )}
                                                <img src={`${API_URL}/${item.image1}`} alt={item.name} className="w-100" />
                                                <label>{item.name.toUpperCase()}</label>
                                            </Link>
                                            <div className='d-flex justify-content-center align-items-center gap-1 mb-1' style={{ minHeight: '20px' }}>
                                                {stars}
                                            </div>
                                            <div className='d-flex justify-content-between align-items-center mb-1 w-100' style={{fontSize:'13px',textAlign:'left'}}>
                                                <div className='d-flex align-items-center flex-wrap'>
                                                    <strong className='text-danger me-1' style={{fontSize:'15px'}}>₹{price}</strong>
                                                    {hasDiscount && <span className='text-muted text-decoration-line-through' style={{ fontSize: '12px' }}>₹{original}</span>}
                                                </div>
                                                {item.quantity && item.unit ? (
                                                    <span className='text-muted small'>{item.quantity}{item.unit}</span>
                                                ) : <span className='invisible small'>.</span>}
                                            </div>
                                            {!inCart ? (
                                                <button onClick={() => handleAddToCart(item)}>ADD TO CART</button>
                                            ) : (
                                                <div className='qty-bar'>
                                                    <button type='button' aria-label='Decrease quantity' onClick={() => handleDec(cartEntry._id)}>-</button>
                                                    <span className='qty-mid'>{cartEntry.quantity}</span>
                                                    <button type='button' aria-label='Increase quantity' onClick={() => handleInc(cartEntry._id)}>+</button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-center">No similar products found.</p>
                            )}
                        </div>
                        <div className="buy-row">
                            <button className="btn btn-primary" onClick={() => handleAddToCart(productData)}>ADD TO CART</button>
                        </div>
                    </div>
                </div>
            )}
            <div className='mt-5 mb-5 pt-5'></div>
            <ToastContainer />
        </Fragment>
    );
}

export default ProductPage;
